import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, CheckCircle2, ChevronDown, Copy, Eye, EyeOff, Pencil, PlugZap, Plus, RefreshCw, ScrollText, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { ChannelLogo } from "@/components/ChannelLogo";
import { MarketplaceConnectDialog } from "@/components/Marketplaces";
import { SftpConnectDialog } from "@/components/Sftp";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, isDemo, isMarketplace, type ChannelItem } from "@/lib/api";
import { t, msg } from "@/lib/i18n";
import { fmtNumber, fmtRelative } from "@/lib/format";

const SHOP = /^[a-z0-9][a-z0-9-]*$/;

export function ChannelsPage() {
  const { tenantId } = useTenant();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ["channels", tenantId], queryFn: () => api.channels.list(tenantId) });
  const open = params.get("connect") === "1";
  const [sftpOpen, setSftpOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const setOpen = (o: boolean) => setParams(o ? { connect: "1" } : {}, { replace: true });

  // Rücksprung vom Server: verständlicher Hinweis statt Fehlerseite, bei fehlender App direkt zurück in den Dialog
  useEffect(() => {
    const err = params.get("error");
    if (!err) return;
    const messages: Record<string, string> = {
      shopify_app_missing: "Für diesen Shop sind noch keine App-Zugangsdaten hinterlegt.",
      shopify_not_configured: "Für diesen Shop sind noch keine App-Zugangsdaten hinterlegt.",
      shopify_token: "Shopify hat die Anmeldung nicht bestätigt. Bitte Client-ID und Secret prüfen und erneut verbinden.",
      shop_belongs_to_other_tenant: "Dieser Shop ist bereits mit einem anderen Kunden verbunden.",
    };
    toast.error(messages[err] ?? "Verbinden fehlgeschlagen");
    setParams(err === "shop_belongs_to_other_tenant" ? {} : { connect: "1" }, { replace: true });
  }, [params, setParams]);

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Shops")}
        description={t("Verbundene Verkaufskanäle. Artikel, Aufträge, Bestände und Tracking werden automatisch synchronisiert.")}
        actions={<Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setMarketOpen(true)}>{t("Kanal verbinden")}</Button>}
      />
      <div className="px-4 md:px-8 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {q.isLoading && [0, 1].map((i) => <Card key={i} className="p-5"><Skeleton className="h-5 w-40" /><Skeleton className="h-4 w-56 mt-3" /><Skeleton className="h-16 w-full mt-4" /></Card>)}
        {q.data?.map((c) => (
          <Card key={c.id} className="p-5">
            <div className="flex items-start gap-3">
              <ChannelLogo kind={c.kind} />
              <Link to={`/channels/${c.id}`} className="min-w-0 flex-1 group">
                <p className="font-semibold group-hover:underline underline-offset-2 truncate">{c.name}</p>
                <p className="text-[12px] text-fg-muted truncate">{c.externalAccount}</p>
              </Link>
              <div className="flex items-center gap-1.5 shrink-0">
                {c.kind === "sftp" || isMarketplace(c.kind) ? (c.status === "active" ? <Badge tone="success">{t("aktiv")}</Badge> : <Badge tone="warning">{t("pausiert")}</Badge>) : c.status === "active" ? <Badge tone="success">{t("verbunden")}</Badge> : c.status === "pending" ? <Badge tone="warning">{t("wird eingerichtet")}</Badge> : <Badge tone="danger">{t("getrennt")}</Badge>}
                <Link to={`/channels/${c.id}`} aria-label={t("Einstellungen bearbeiten")} title={t("Einstellungen bearbeiten")} className="size-8 grid place-items-center rounded-lg text-fg-muted hover:text-fg hover:bg-surface-2"><Pencil className="size-4" /></Link>
                <RemoveChannelButton c={c} />
              </div>
            </div>
            {c.lastError && <p className="mt-3 text-[12.5px] text-danger bg-danger-soft rounded-lg px-3 py-2">{c.lastError}</p>}
            {c.kind === "sftp" || isMarketplace(c.kind) ? (
              <div className="mt-4"><Link to={`/channels/${c.id}`}><Button size="sm" icon={<ScrollText className="size-3.5" />}>{t("Protokoll")}</Button></Link></div>
            ) : (<>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-[12.5px]">
              <div><dt className="text-fg-subtle">{t("Artikel")}</dt><dd className="font-medium tabular-nums">{fmtNumber(c.productCount)}</dd></div>
              <div><dt className="text-fg-subtle">{t("Letzter Abgleich")}</dt><dd className="font-medium">{fmtRelative(c.lastSyncAt)}</dd></div>
            </dl>
            <div className="mt-4 flex gap-2">
              {c.status === "active" ? (
                <Link to={`/channels/${c.id}?sync=1`}><Button size="sm" icon={<RefreshCw className="size-3.5" />}>{t("Synchronisation")}</Button></Link>
              ) : (
                <Button size="sm" variant="primary" icon={<PlugZap className="size-3.5" />} onClick={() => setOpen(true)}>{t("Neu verbinden")}</Button>
              )}
            </div>
            </>)}
          </Card>
        ))}
        {q.data?.length === 0 && (
          <Card className="md:col-span-2 xl:col-span-3">
            <EmptyState icon={<PlugZap className="size-5" />} title={t("Noch kein Shop verbunden")} action={<Button variant="primary" onClick={() => setMarketOpen(true)}>{t("Ersten Kanal verbinden")}</Button>}>
              {t("In zwei Minuten eingerichtet – Aufträge, Tracking und Bestände laufen danach automatisch.")}
            </EmptyState>
          </Card>
        )}
      </div>
      <SftpConnectDialog open={sftpOpen} onOpenChange={setSftpOpen} />
      <MarketplaceConnectDialog open={marketOpen} onOpenChange={setMarketOpen} onShopify={() => setOpen(true)} onSftp={() => setSftpOpen(true)} />
      <ConnectDialog open={open} onOpenChange={setOpen} tenantId={tenantId} />
    </div>
  );
}

/** Muss zu SHOPIFY_REQUESTED_SCOPES (connector-shopify) passen – Pflicht- und empfohlene Berechtigungen */
const SCOPES = "read_products,write_products,read_inventory,write_inventory,read_locations,read_orders,read_assigned_fulfillment_orders,write_assigned_fulfillment_orders,read_third_party_fulfillment_orders,write_third_party_fulfillment_orders,read_fulfillments,write_fulfillments,read_returns,write_returns,read_all_orders,write_shipping,read_merchant_managed_fulfillment_orders,write_orders";

function CopyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-1.5">
      <div className="text-[11.5px] text-fg-subtle">{t(label)}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <code className="min-w-0 flex-1 truncate rounded-md bg-surface-2 border border-border px-2 py-1 font-mono text-[11.5px]">{value}</code>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(value).then(() => toast.success(`${label} kopiert`), () => undefined)}
          className="size-7 shrink-0 grid place-items-center rounded-md border border-border hover:bg-surface-2"
          aria-label={t("{label} kopieren", { label })}
        >
          <Copy className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Shop verbinden in zwei Schritten: Shop-Adresse → Zugangsdaten der Shopify-App dieses Kunden.
 * Jeder Kunde hat seine eigene Custom App; die Zugangsdaten werden verschlüsselt beim Shop gespeichert.
 * Gibt es eine gemeinsame (später öffentliche) Logiful-App, ist Schritt 2 optional.
 */
function ConnectDialog({ open, onOpenChange, tenantId }: { open: boolean; onOpenChange: (o: boolean) => void; tenantId: string }) {
  const { me } = useTenant();
  const globalApp = me.features?.shopify === true;
  const [step, setStep] = useState<1 | 2>(1);
  const [shop, setShop] = useState("");
  const [ownApp, setOwnApp] = useState(!globalApp);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [guide, setGuide] = useState(false);
  const handle = shop.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\.myshopify\.com.*$/, "").replace(/\/.*$/, "");
  const validShop = SHOP.test(handle);
  const credsComplete = clientId.trim().length >= 10 && clientSecret.trim().length >= 16;
  const canConnect = validShop && (!ownApp || credsComplete);
  const origin = location.origin;

  useEffect(() => {
    if (!open) { setStep(1); setShowSecret(false); setGuide(false); }
  }, [open]);

  const connect = useMutation({
    mutationFn: () => api.channels.prepareShopify(tenantId, { shop: `${handle}.myshopify.com`, ...(ownApp ? { clientId: clientId.trim(), clientSecret: clientSecret.trim() } : {}) }),
    onSuccess: (r) => {
      if (isDemo) {
        toast.success(`Demo: Weiterleitung zu ${r.shop}`, { description: msg("Im Echtbetrieb bestätigst du jetzt die Berechtigungen in Shopify.") });
        onOpenChange(false);
        return;
      }
      location.href = r.installUrl;
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Verbinden fehlgeschlagen") : "Verbinden fehlgeschlagen"),
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <div className="flex items-center gap-2 text-[12px] text-fg-subtle">
            <span className={cx(step === 1 && "text-fg font-medium")}>{t("1 Shop")}</span>
            <span className="w-4 h-px bg-border-strong" />
            <span className={cx(step === 2 && "text-fg font-medium")}>{t("2 Shopify-App")}</span>
            <span className="w-4 h-px bg-border-strong" />
            <span>{t("3 In Shopify bestätigen")}</span>
          </div>
          <Dialog.Title className="mt-3 text-[17px] font-semibold">{t("Shopify-Shop verbinden")}</Dialog.Title>

          {step === 1 ? (
            <>
              <Dialog.Description className="text-fg-muted mt-1">{t("Welcher Shop soll angebunden werden?")}</Dialog.Description>
              <div className="mt-5">
                <label className="text-[12px] font-medium text-fg-muted">{t("Shop-Adresse")}</label>
                <Input autoFocus value={shop} onChange={(e) => setShop(e.target.value)} onKeyDown={(e) => e.key === "Enter" && validShop && setStep(2)} placeholder={t("mein-shop")} suffix={t(".myshopify.com")} className="mt-1" invalid={shop.length > 0 && !validShop} />
                <p className="mt-1.5 text-[12px] text-fg-subtle">{t("Zu finden in Shopify unter Einstellungen → Domains. Eine vollständige URL funktioniert auch.")}</p>
              </div>
              <ul className="mt-5 space-y-2 text-[12.5px] text-fg-muted">
                {[msg("Artikel werden importiert und geprüft"), msg("Aufträge laufen automatisch ins Lager"), msg("Bestände und Tracking fließen zurück in Shopify")].map((line) => (
                  <li key={line} className="flex items-center gap-2"><CheckCircle2 className="size-4 text-success" /> {t(line)}</li>
                ))}
              </ul>
              <div className="mt-6 flex justify-end gap-2">
                <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
                <Button variant="primary" disabled={!validShop} onClick={() => setStep(2)}>{t("Weiter")}</Button>
              </div>
            </>
          ) : (
            <>
              <Dialog.Description className="text-fg-muted mt-1">
                {t("Jeder Shop wird über eine eigene Shopify-App angebunden. Die Zugangsdaten bleiben verschlüsselt bei")} <b className="text-fg">{handle}{t(".myshopify.com")}</b> {t("und sind für andere Kunden nicht sichtbar.")}
              </Dialog.Description>

              {globalApp && (
                <button type="button" role="switch" aria-checked={ownApp} onClick={() => setOwnApp(!ownApp)} className="mt-4 flex items-center gap-2.5 text-left">
                  <span className={cx("w-8 h-[18px] shrink-0 rounded-full p-0.5 transition-colors", ownApp ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", ownApp && "translate-x-3.5")} /></span>
                  <span className="text-[13px]"><span className="font-medium">{t("Eigene App des Kunden verwenden")}</span> <span className="text-fg-subtle">{t("· sonst die Logiful-App")}</span></span>
                </button>
              )}

              {ownApp && (
                <div className="mt-4 space-y-3">
                  <label className="block">
                    <span className="text-[12px] font-medium text-fg-muted">{t("Client-ID")}</span>
                    <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="z. B. 3f8a…" className="mt-1 font-mono" autoComplete="off" spellCheck={false} />
                  </label>
                  <label className="block">
                    <span className="text-[12px] font-medium text-fg-muted">{t("Client-Secret")}</span>
                    <Input
                      type={showSecret ? "text" : "password"}
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder={t("shpss_…")}
                      className="mt-1 font-mono"
                      autoComplete="off"
                      spellCheck={false}
                      suffix={<button type="button" onClick={() => setShowSecret(!showSecret)} className="hover:text-fg" aria-label={showSecret ? t("Secret verbergen") : t("Secret anzeigen")}>{showSecret ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}</button>}
                    />
                  </label>

                  <div className="rounded-xl border border-border">
                    <button type="button" onClick={() => setGuide(!guide)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-[13px] font-medium">
                      <BookOpen className="size-4 text-fg-subtle" /> {t("So wird die App im Shopify Dev Dashboard angelegt")}
                      <ChevronDown className={cx("ml-auto size-4 text-fg-subtle transition-transform", guide && "rotate-180")} />
                    </button>
                    {guide && (
                      <ol className="px-3 pb-3 space-y-3 text-[12.5px] text-fg-muted list-decimal list-inside marker:text-fg-subtle">
                        <li>{t("Unter")} <a className="underline hover:text-fg" href="https://dev.shopify.com/dashboard" target="_blank" rel="noreferrer">{t("dev.shopify.com")}</a> {t("→ Apps →")} <b className="text-fg">{t("App erstellen")}</b>{t(", Name z. B. „Logiful Connect“.")}</li>
                        <li>
                          {t("Beim Erstellen einer Version eintragen:")}
                          <CopyValue label={t("App-URL")} value={origin} />
                          <CopyValue label={t("Weiterleitungs-URL")} value={`${origin}/shopify/callback`} />
                          <CopyValue label={t("Zugriffsbereiche (Scopes)")} value={SCOPES} />
                        </li>
                        <li>{t("Version freigeben, dann unter")} <b className="text-fg">{t("Vertrieb")}</b> {t("„Benutzerdefinierter Vertrieb“ wählen und")} <b className="text-fg">{handle}{t(".myshopify.com")}</b> {t("eintragen.")}</li>
                        <li>{t("Unter")} <b className="text-fg">{t("Einstellungen")}</b> {t("Client-ID und Secret kopieren und oben einfügen. Webhooks richtet das Portal danach automatisch ein.")}</li>
                      </ol>
                    )}
                  </div>
                </div>
              )}

              <p className="mt-4 flex items-center gap-2 text-[12px] text-fg-subtle"><ShieldCheck className="size-4" /> {t("Danach geht es zu Shopify – dort bestätigt der Shop-Inhaber die Berechtigungen.")}</p>
              <div className="mt-5 flex justify-between gap-2">
                <Button variant="ghost" onClick={() => setStep(1)}>{t("Zurück")}</Button>
                <div className="flex gap-2">
                  <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
                  <Button variant="primary" disabled={!canConnect} loading={connect.isPending} onClick={() => connect.mutate()}>{t("Weiter zu Shopify")}</Button>
                </div>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Kanal entfernen (nur mit Verwaltungsrecht): ohne Aufträge wird er gelöscht (z. B. versehentlich angelegt),
 * mit Aufträgen nur getrennt – Abrufe stoppen, die Auftragshistorie bleibt.
 */
function RemoveChannelButton({ c }: { c: ChannelItem }) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const rm = useMutation({
    mutationFn: () => api.channels.remove(tenantId, c.id),
    onSuccess: (r) => {
      toast.success(r.result === "deleted" ? t("„{name}“ entfernt", { name: c.name }) : t("„{name}“ getrennt", { name: c.name }), {
        description: r.result === "deleted" ? undefined : t("Es werden keine Aufträge und Bestände mehr abgeglichen. Die {n} Aufträge bleiben erhalten.", { n: r.orders }),
      });
      void qc.invalidateQueries({ queryKey: ["channels", tenantId] });
    },
    onError: (e) => toast.error(t("Nicht entfernt"), { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" }),
  });
  if (!canManage(role)) return null;
  const ask = () => {
    const msg = c.kind === "shopify"
      ? t("„{name}“ entfernen? Ohne Aufträge wird die Verbindung gelöscht, mit Aufträgen nur getrennt (die Auftragshistorie bleibt). Die Logiful-App im Shopify-Admin bitte zusätzlich deinstallieren.", { name: c.name })
      : t("„{name}“ entfernen? Ohne Aufträge wird die Verbindung gelöscht, mit Aufträgen nur getrennt (die Auftragshistorie bleibt).", { name: c.name });
    if (confirm(msg)) rm.mutate();
  };
  return (
    <button onClick={ask} disabled={rm.isPending} aria-label={t("Verbindung entfernen")} title={t("Verbindung entfernen")} className="size-8 grid place-items-center rounded-lg text-fg-muted hover:text-danger hover:bg-surface-2 disabled:opacity-50">
      <Trash2 className="size-4" />
    </button>
  );
}
